/**
 * Branded navbar for /new — the first Dreambooth surface many creators see.
 * Dark glass bar: wordmark left, profile (or sign-in) right. Mirrors the
 * landing-page navbar language (dreamboothstudio.com) on the booth's dark
 * theme.
 */

import React from 'react'
import { FaGoogle } from 'react-icons/fa'
import type { StudioSessionUser } from '../onboardingApi'
import { useCopy } from '../regionCopy'
import LangToggle from './LangToggle'

const STUDIO_URL = (): string => process.env.VITE_API_URL || 'https://dreamboothstudio.com'

interface NewNavbarProps {
  user: StudioSessionUser | null
  onSignIn: () => void
}

export const NewNavbar: React.FC<NewNavbarProps> = ({ user, onSignIn }) => {
  const c = useCopy()
  return (
    <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-black/70 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-6">
        <a href="/new" className="flex items-center gap-2">
          <img
            src="/assets/images/dreambooth-white.png"
            alt="Dreambooth"
            className="h-7 w-auto"
            draggable={false}
          />
        </a>

        <div className="flex items-center gap-3">
          <LangToggle />
          {user ? (
            <a
              href={`${STUDIO_URL()}/dashboard`}
              target="_blank"
              rel="noreferrer"
              className="hidden text-sm text-whiteout/50 transition-colors hover:text-whiteout sm:block"
            >
              {c.dashboard}
            </a>
          ) : (
            <a
              href={`${STUDIO_URL()}/`}
              target="_blank"
              rel="noreferrer"
              className="hidden text-sm text-whiteout/50 transition-colors hover:text-whiteout sm:block"
            >
              {c.learnMore}
            </a>
          )}
          {user ? (
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] py-1 pl-1 pr-3">
              {user.image ? (
                <img
                  src={user.image}
                  alt={user.name || user.email}
                  referrerPolicy="no-referrer"
                  className="h-7 w-7 rounded-full ring-1 ring-white/20"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-purpleSnail to-luridRed text-xs font-bold text-whiteout">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="max-w-[140px] truncate text-xs text-whiteout/80">
                {user.name || user.email}
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onSignIn}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-whiteout/80 transition-colors hover:border-white/30 hover:text-whiteout"
            >
              <FaGoogle className="text-[10px]" /> {c.signIn}
            </button>
          )}
        </div>
      </div>
    </header>
  )
}

export default NewNavbar
